import type { getMaterialBasePricesParams } from "@/api/basePriceService";
import type { flagPartParam } from "@/api/flaggedPartService";
import type { getMasterParams } from "@/api/masterService";
import type { MaterialSupplierPayload } from "@/api/materialSupplierService";
import type { ManualMasterPartListParams } from "@/api/manualMasterPartService";
import type {
  PAPartRequestParam,
  PASupplierRequestParam,
} from "@/api/PAService";

export const queryKeys = {
  user: {
    all: ["users"] as const,
    me: () => [...queryKeys.user.all, "me"] as const,
    permission: () => [...queryKeys.user.all, "permission"] as const,
    list: () => [...queryKeys.user.all, "list"] as const,
    create: () => [...queryKeys.user.all, "create"] as const,
    update: () => [...queryKeys.user.all, "update"] as const,
  },
  group: {
    all: ["groups"] as const,
    list: () => [...queryKeys.group.all, "list"] as const,
    create: () => [...queryKeys.group.all, "create"] as const,
    insert: () => [...queryKeys.group.all, "insert"] as const,
    update: () => [...queryKeys.user.all, "update"] as const,
    resource: () => [...queryKeys.group.all, "resource"] as const,
    action: () => [...queryKeys.group.all, "action"] as const,
    groupResourceActionMapping: () =>
      [...queryKeys.group.all, "group-resource-action-mapping"] as const,
  },
  materialTypes: {
    all: ["materialTypes"] as const,
    list: () => [...queryKeys.materialTypes.all, "list"] as const,
    specs: (materialTypeId: number) =>
      [...queryKeys.materialTypes.all, materialTypeId, "specs"] as const,
    create: () => [...queryKeys.materialTypes.all, "create"] as const,
    update: () => [...queryKeys.materialTypes.all, "update"] as const,
    createSpec: () => [...queryKeys.materialTypes.all, "create-spec"] as const,
    updateSpec: () => [...queryKeys.materialTypes.all, "update-spec"] as const,
    listAllSpec: () =>
      [...queryKeys.materialTypes.all, "list-all-spec"] as const,
  },
  master: {
    all: ["masterData"] as const,
    dimensions: () => [...queryKeys.master.all, "dimensions"] as const,
    supplier2nd: () => [...queryKeys.master.all, "supplier2nd"] as const,
    returnScrap: () => [...queryKeys.master.all, "returnScrap"] as const,
  },
  masterProcess: {
    all: ["masterProcess"] as const,
    getOEMProcesses: () =>
      [...queryKeys.masterProcess.all, "oemProcesses", "list"] as const,
    createOEMProcess: () =>
      [...queryKeys.masterProcess.all, "oemProcesses", "create"] as const,
    updateOEMProcess: () =>
      [...queryKeys.masterProcess.all, "oemProcesses", "update"] as const,
    getOEMPartProcesses: () =>
      [...queryKeys.masterProcess.all, "oemPartProcesses", "list"] as const,
    createOEMPartProcess: () =>
      [...queryKeys.masterProcess.all, "oemPartProcesses", "create"] as const,
    updateOEMPartProcess: () =>
      [...queryKeys.masterProcess.all, "oemPartProcesses", "update"] as const,
    getOEMPartOtherProcesses: () =>
      [
        ...queryKeys.masterProcess.all,
        "oemPartOtherProcesses",
        "list",
      ] as const,
    createOEMPartOtherProcess: () =>
      [
        ...queryKeys.masterProcess.all,
        "oemPartOtherProcesses",
        "create",
      ] as const,
    updateOEMPartOtherProcess: () =>
      [
        ...queryKeys.masterProcess.all,
        "oemPartOtherProcesses",
        "update",
      ] as const,
    getSpareProcesses: () =>
      [...queryKeys.masterProcess.all, "spareProcesses", "list"] as const,
    createSpareProcess: () =>
      [...queryKeys.masterProcess.all, "spareProcesses", "create"] as const,
    updateSpareProcess: () =>
      [...queryKeys.masterProcess.all, "spareProcesses", "update"] as const,
    getSparePartProcesses: () =>
      [...queryKeys.masterProcess.all, "sparePartProcesses", "list"] as const,
    createSparePartProcess: () =>
      [...queryKeys.masterProcess.all, "sparePartProcesses", "create"] as const,
    updateSparePartProcess: () =>
      [...queryKeys.masterProcess.all, "sparePartProcesses", "update"] as const,
    getSparePartOtherProcesses: () =>
      [
        ...queryKeys.masterProcess.all,
        "sparePartOtherProcesses",
        "list",
      ] as const,
    createSparePartOtherProcess: () =>
      [
        ...queryKeys.masterProcess.all,
        "sparePartOtherProcesses",
        "create",
      ] as const,
    updateSparePartOtherProcess: () =>
      [
        ...queryKeys.masterProcess.all,
        "sparePartOtherProcesses",
        "update",
      ] as const,
    getMaterialOtherProcesses: () =>
      [
        ...queryKeys.masterProcess.all,
        "materialOtherProcesses",
        "list",
      ] as const,
    createMaterialOtherProcess: () =>
      [
        ...queryKeys.masterProcess.all,
        "materialOtherProcesses",
        "create",
      ] as const,
    updateMaterialOtherProcess: () =>
      [
        ...queryKeys.masterProcess.all,
        "materialOtherProcesses",
        "update",
      ] as const,
    getMaterial2ndTierProcesses: () =>
      [
        ...queryKeys.masterProcess.all,
        "material2ndTierProcesses",
        "list",
      ] as const,
    createMaterial2ndTierProcess: () =>
      [
        ...queryKeys.masterProcess.all,
        "material2ndTierProcesses",
        "create",
      ] as const,
    updateMaterial2ndTierProcess: () =>
      [
        ...queryKeys.masterProcess.all,
        "material2ndTierProcesses",
        "update",
      ] as const,
    getChildPartProcesses: () =>
      [...queryKeys.masterProcess.all, "childPartProcesses", "list"] as const,
    createChildPartProcess: () =>
      [...queryKeys.masterProcess.all, "childPartProcesses", "create"] as const,
    updateChildPartProcess: () =>
      [...queryKeys.masterProcess.all, "childPartProcesses", "update"] as const,
    getSupplier2nd: () =>
      [...queryKeys.masterProcess.all, "supplier2nd", "list"] as const,
    createSupplier2nd: () =>
      [...queryKeys.masterProcess.all, "supplier2nd", "create"] as const,
    updateSupplier2nd: () =>
      [...queryKeys.masterProcess.all, "supplier2nd", "update"] as const,
    getReturnScrap: (params: getMasterParams) =>
      [...queryKeys.masterProcess.all, "returnScrap", "list", params] as const,
    createReturnScrap: () =>
      [...queryKeys.masterProcess.all, "returnScrap", "create"] as const,
    updateReturnScrap: () =>
      [...queryKeys.masterProcess.all, "returnScrap", "update"] as const,
    getReturnScrapPrice: (year: number) =>
      [
        ...queryKeys.masterProcess.all,
        "returnScrapPrice",
        "list",
        year,
      ] as const,
    createReturnScrapPrice: () =>
      [...queryKeys.masterProcess.all, "returnScrapPrice", "create"] as const,
    updateReturnScrapPrice: () =>
      [...queryKeys.masterProcess.all, "returnScrapPrice", "update"] as const,
    addQuarterScrapPrice: () =>
      [
        ...queryKeys.masterProcess.all,
        "returnScrapPrice",
        "addQuarter",
      ] as const,
    getReasonPriceChange: () =>
      [...queryKeys.masterProcess.all, "reasonPriceChange", "list"] as const,
    createReasonPriceChange: () =>
      [...queryKeys.masterProcess.all, "reasonPriceChange", "create"] as const,
    updateReasonPriceChange: () =>
      [...queryKeys.masterProcess.all, "reasonPriceChange", "update"] as const,
  },
  masterBuyerBoss: {
    all: ["masterBuyerBoss"] as const,
    list: () => [...queryKeys.masterBuyerBoss.all, "list"] as const,
    withBuyerCode: () =>
      [...queryKeys.masterBuyerBoss.all, "withBuyerCode"] as const,
    update: (employee_id: string) =>
      [...queryKeys.masterBuyerBoss.all, "update", employee_id] as const,
    delete: (employee_id: string) =>
      [...queryKeys.masterBuyerBoss.all, "delete", employee_id] as const,
    allBuyerCodes: () =>
      [...queryKeys.masterBuyerBoss.all, "allBuyerCodes"] as const,
  },
  materialSupplier: {
    all: ["materialSuppliers"] as const,
    listAll: () => [...queryKeys.materialSupplier.all, "list-all"] as const,
    list: (params: MaterialSupplierPayload) =>
      [...queryKeys.materialSupplier.all, "list", params] as const,
    formulars: () => [...queryKeys.materialSupplier.all, "formulars"] as const,
    detail: (id?: number) =>
      [...queryKeys.materialSupplier.all, "detail", id] as const,
    create: () => [...queryKeys.materialSupplier.all, "create"] as const,
    update: (id: number) =>
      [...queryKeys.materialSupplier.all, "update", id] as const,
    materialCodeById: (id?: number) =>
      [
        ...queryKeys.materialSupplier.all,
        "materialCode",
        "detail",
        id,
      ] as const,
    materialCodes: (
      material_supplier_id?: number,
      is_active?: boolean,
      material_dimension_id?: number,
      skip?: number,
      limit?: number,
      search?: string,
    ) =>
      [
        ...queryKeys.materialSupplier.all,
        "materialCode",
        "list",
        material_supplier_id,
        is_active,
        material_dimension_id,
        skip,
        limit,
        search,
      ] as const,
    materialCodesTotal: (material_supplier_id?: number, is_active?: boolean) =>
      [
        ...queryKeys.materialSupplier.all,
        "materialCode",
        "total",
        material_supplier_id,
        is_active,
      ] as const,
    createMaterialCode: () =>
      [...queryKeys.materialSupplier.all, "materialCode", "create"] as const,
    updateMaterialCode: () =>
      [...queryKeys.materialSupplier.all, "materialCode", "update"] as const,
    getOtherCostProcesses: (materialSupplierId?: number) =>
      [
        ...queryKeys.materialSupplier.all,
        "otherCostProcesses",
        materialSupplierId,
      ] as const,
    createOtherCostProcess: () =>
      [
        ...queryKeys.materialSupplier.all,
        "otherCostProcesses",
        "create",
      ] as const,
    updateOtherCostProcess: (id: number) =>
      [
        ...queryKeys.materialSupplier.all,
        "otherCostProcesses",
        "update",
        id,
      ] as const,
    deleteOtherCostProcess: (id: number) =>
      [
        ...queryKeys.materialSupplier.all,
        "otherCostProcesses",
        "delete",
        id,
      ] as const,
    materialCodesBySupplier: (supplierCode: string) =>
      [
        ...queryKeys.materialSupplier.all,
        "materialCode",
        "bySupplier",
        supplierCode,
      ] as const,
    materialDetailByPartId: (partId: number) =>
      [
        ...queryKeys.materialSupplier.all,
        "materialDetail",
        "byPartId",
        partId,
      ] as const,
  },
  firstTierSupplier: {
    all: ["firstTierSuppliers"] as const,
    list: () => [...queryKeys.firstTierSupplier.all, "list"] as const,
    edit: () => [...queryKeys.firstTierSupplier.all, "edit"] as const,
    partList(supplierCode: string, version: string, isActive?: boolean) {
      return [
        ...queryKeys.firstTierSupplier.all,
        "partsBySupplier",
        supplierCode,
        isActive,
        version,
      ] as const;
    },
    partSKPList(supplierCode: string) {
      return [
        ...queryKeys.firstTierSupplier.all,
        "partSKPBySupplier",
        supplierCode,
      ] as const;
    },
    insertPart: () =>
      [...queryKeys.firstTierSupplier.all, "insertPart"] as const,
    updatePart: () =>
      [...queryKeys.firstTierSupplier.all, "updatePart"] as const,
    duplicateParts: () =>
      [...queryKeys.firstTierSupplier.all, "duplicateParts"] as const,
  },
  breakdown: {
    all: ["breakdowns"] as const,
    childPartV1: (partId: number) =>
      [...queryKeys.breakdown.all, "childPartV1", partId] as const,
    childPartV2: (partId: number) =>
      [...queryKeys.breakdown.all, "childPartV2", partId] as const,
    createChildPartV2: () =>
      [...queryKeys.breakdown.all, "createChildPartV2"] as const,
    updateChildPartV2: () =>
      [...queryKeys.breakdown.all, "updateChildPartV2"] as const,
    deleteChildPartV2: () =>
      [...queryKeys.breakdown.all, "deleteChildPartV2"] as const,
    breakdownProcessV1: (supplierCode: string) =>
      [...queryKeys.breakdown.all, "breakdownProcessV1", supplierCode] as const,
    breakdownProcessV2: (partId: number) =>
      [...queryKeys.breakdown.all, "breakdownProcessV2", partId] as const,
    partProcessV2: (partId: number) =>
      [...queryKeys.breakdown.all, "partProcessV2", partId] as const,
    partOtherProcessV2: (partId: number) =>
      [...queryKeys.breakdown.all, "partOtherProcessV2", partId] as const,
    updatePartProcessV2Detail: () =>
      [...queryKeys.breakdown.all, "updatePartProcessV2Detail"] as const,
    updatePartOtherProcessV2Detail: () =>
      [...queryKeys.breakdown.all, "updatePartOtherProcessV2Detail"] as const,
    partPriceHistory: (partId: number, version: "v1" | "v2") =>
      [
        ...queryKeys.breakdown.all,
        "partPriceHistory",
        partId,
        version,
      ] as const,
    createMaterialProcessV2: () =>
      [...queryKeys.breakdown.all, "createMaterialProcessV2"] as const,
    createChildPartProcessV2: () =>
      [...queryKeys.breakdown.all, "createChildPartProcessV2"] as const,
    createPartProcessV2: () =>
      [...queryKeys.breakdown.all, "createPartProcessV2"] as const,
    createPartOtherProcessV2: () =>
      [...queryKeys.breakdown.all, "createPartOtherProcessV2"] as const,
    updateMaterialProcessV2: () =>
      [...queryKeys.breakdown.all, "updateMaterialProcessV2"] as const,
    updateChildPartProcessV2: () =>
      [...queryKeys.breakdown.all, "updateChildPartProcessV2"] as const,
    updatePartProcessV2: () =>
      [...queryKeys.breakdown.all, "updatePartProcessV2"] as const,
    updatePartOtherProcessV2: () =>
      [...queryKeys.breakdown.all, "updatePartOtherProcessV2"] as const,
    deleteMaterialProcessV2: () =>
      [...queryKeys.breakdown.all, "deleteMaterialProcessV2"] as const,
    deleteChildPartProcessV2: () =>
      [...queryKeys.breakdown.all, "deleteChildPartProcessV2"] as const,
    deletePartProcessV2: () =>
      [...queryKeys.breakdown.all, "deletePartProcessV2"] as const,
    deletePartOtherProcessV2: () =>
      [...queryKeys.breakdown.all, "deletePartOtherProcessV2"] as const,
  },
  breakdownExchangeRate: {
    all: ["breakdownExchangeRates"] as const,
    master: () => [...queryKeys.breakdownExchangeRate.all, "master"] as const,
    parts: (supplierCode: string, isActive: boolean) =>
      [
        ...queryKeys.breakdownExchangeRate.all,
        "parts",
        supplierCode,
        isActive,
      ] as const,
    createPart: () =>
      [...queryKeys.breakdownExchangeRate.all, "createPart"] as const,
    updatePart: () =>
      [...queryKeys.breakdownExchangeRate.all, "updatePart"] as const,
    bulkImportParts: () =>
      [...queryKeys.breakdownExchangeRate.all, "bulkImportParts"] as const,
    partHistory: (partId: number) =>
      [
        ...queryKeys.breakdownExchangeRate.all,
        "parts",
        "history",
        partId,
      ] as const,
    process: (supplierCode: string) =>
      [
        ...queryKeys.breakdownExchangeRate.all,
        "process",
        supplierCode,
      ] as const,
    createOEMOtherProcess: () =>
      [
        ...queryKeys.breakdownExchangeRate.all,
        "createOEMOtherProcess",
      ] as const,
    createSpareOtherProcess: () =>
      [
        ...queryKeys.breakdownExchangeRate.all,
        "createSpareOtherProcess",
      ] as const,
    updateOEMOtherProcess: () =>
      [
        ...queryKeys.breakdownExchangeRate.all,
        "updateOEMOtherProcess",
      ] as const,
    updateSpareOtherProcess: () =>
      [
        ...queryKeys.breakdownExchangeRate.all,
        "updateSpareOtherProcess",
      ] as const,
    deleteOEMOtherProcess: () =>
      [
        ...queryKeys.breakdownExchangeRate.all,
        "deleteOEMOtherProcess",
      ] as const,
    deleteSpareOtherProcess: () =>
      [
        ...queryKeys.breakdownExchangeRate.all,
        "deleteSpareOtherProcess",
      ] as const,
  },
  basePrice: {
    all: ["basePrices"] as const,
    list: (params: getMaterialBasePricesParams) =>
      [...queryKeys.basePrice.all, "list", params] as const,
  },
  exchangeRate: {
    all: ["exchangeRates"] as const,
    list: (year: number) =>
      [...queryKeys.exchangeRate.all, "list", { year }] as const,
    update: () => [...queryKeys.exchangeRate.all, "update"] as const,
    createQuarter: () =>
      [...queryKeys.exchangeRate.all, "createQuarter"] as const,
    suppliers: () => [...queryKeys.exchangeRate.all, "suppliers"] as const,
    createSupplier: () =>
      [...queryKeys.exchangeRate.all, "createSupplier"] as const,
    updateSupplier: () =>
      [...queryKeys.exchangeRate.all, "updateSupplier"] as const,
    currencies: () => [...queryKeys.exchangeRate.all, "currencies"] as const,
    createCurrency: () =>
      [...queryKeys.exchangeRate.all, "createCurrency"] as const,
    updateCurrency: () =>
      [...queryKeys.exchangeRate.all, "updateCurrency"] as const,
  },
  flaggedPart: {
    all: ["flaggedParts"] as const,
    list: (params: flagPartParam) =>
      [...queryKeys.flaggedPart.all, "list", params] as const,
    flagPart: () => [...queryKeys.flaggedPart.all, "flagPart"] as const,
    unFlagParts: () => [...queryKeys.flaggedPart.all, "unFlagParts"] as const,
    unflagAll: () => [...queryKeys.flaggedPart.all, "unflagAll"] as const,
    changeToAdminFlagged: () => [...queryKeys.flaggedPart.all, "changeToAdminFlagged"] as const,
    checkVersion: (partId: number) =>
      [...queryKeys.flaggedPart.all, "checkVersion", partId] as const,
  },
  logActivity: {
    all: ["logActivities"] as const,
    detail: (id: number) =>
      [...queryKeys.logActivity.all, "detail", id] as const,
    static: (searchDate: string) =>
      [...queryKeys.logActivity.all, "static", searchDate] as const,
    list: () => [...queryKeys.logActivity.all, "list"] as const,
    resource: () => [...queryKeys.logActivity.all, "resource"] as const,
    entity: () => [...queryKeys.logActivity.all, "entity"] as const,
  },
  pa: {
    all: ["pa"] as const,
    parts: (params: PAPartRequestParam) =>
      [...queryKeys.pa.all, "parts", params] as const,
    suppliers: (params: PASupplierRequestParam) =>
      [...queryKeys.pa.all, "suppliers", params] as const,
    updateSupplierBuyerCode: () =>
      [...queryKeys.pa.all, "updateSupplierBuyerCode"] as const,
  },
  manualMasterPart: {
    all: ["manualMasterParts"] as const,
    search: (partNumber: string) =>
      [...queryKeys.manualMasterPart.all, "search", partNumber] as const,
    list: (params: ManualMasterPartListParams) =>
      [...queryKeys.manualMasterPart.all, "list", params] as const,
    bulkCreate: () =>
      [...queryKeys.manualMasterPart.all, "bulkCreate"] as const,
    delete: () => [...queryKeys.manualMasterPart.all, "delete"] as const,
    bulkDelete: () =>
      [...queryKeys.manualMasterPart.all, "bulkDelete"] as const,
  },
  migration: {
    all: ["migration"] as const,
    legacyMaterialCodes: (
      partNo: string,
      ac: string,
      rt: string,
      supplierCode: string,
    ) =>
      [
        ...queryKeys.migration.all,
        "legacyMaterialCodes",
        partNo,
        ac,
        rt,
        supplierCode,
      ] as const,
    legacyPartActive: (supplierCode: string) =>
      [...queryKeys.migration.all, "legacyPartActive", supplierCode] as const,
  },
} as const;
